/**
 * Thaklis Direct API Service
 *
 * Separate axios client for calling the Thaklis backend directly
 * using per-organization credentials. Only used by the ticket creation page.
 */

import axios from 'axios';
import ApiService from './api.service';

let thaklisClient = null;
let currentMappingId = null;
let currentToken = null;
let currentBaseUrl = null;
let isRefreshing = false;

/**
 * Fetch a valid Thaklis token from the managers backend
 */
async function fetchToken(mappingId) {
  const response = await ApiService.getOrganizationToken(mappingId);
  const data = response?.data?.data;
  if (!data?.token || !data?.thaklisApiUrl) {
    throw new Error('Failed to retrieve Thaklis credentials. Please ensure credentials are configured for this organization.');
  }
  return { token: data.token, thaklisApiUrl: data.thaklisApiUrl };
}

/**
 * Create or reconfigure the axios instance for the Thaklis backend
 */
function createClient(baseURL, token) {
  const client = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  // Response interceptor: retry once on 401
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry && currentMappingId) {
        originalRequest._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const { token: newToken } = await fetchToken(currentMappingId);
            currentToken = newToken;
            // Update default header
            client.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          } finally {
            isRefreshing = false;
          }
        }

        // Retry with new token
        originalRequest.headers['Authorization'] = `Bearer ${currentToken}`;
        return client(originalRequest);
      }

      return Promise.reject(error);
    }
  );

  return client;
}

const ThaklisApiService = {
  /**
   * Initialize the Thaklis API client for a specific organization mapping.
   * Fetches a valid token from the managers backend and configures the axios instance.
   *
   * @param {string} mappingId - The organization mapping ID
   * @returns {Promise<void>}
   */
  initForOrganization: async (mappingId) => {
    const { token, thaklisApiUrl } = await fetchToken(mappingId);
    currentMappingId = mappingId;
    currentToken = token;
    currentBaseUrl = thaklisApiUrl;
    thaklisClient = createClient(thaklisApiUrl, token);
  },

  /**
   * Check if the service is initialized
   */
  isInitialized: () => thaklisClient !== null,

  /**
   * Get the current Thaklis API base URL
   */
  getBaseUrl: () => currentBaseUrl,

  /**
   * Get the current Thaklis auth token (for authenticated image fetching, etc.)
   */
  getToken: () => currentToken,

  /**
   * Reset the service (e.g., when switching organizations or leaving the page)
   */
  reset: () => {
    thaklisClient = null;
    currentMappingId = null;
    currentToken = null;
    currentBaseUrl = null;
  },

  // Generic HTTP methods — all calls go directly to the Thaklis backend
  get: (url, config = {}) => {
    if (!thaklisClient) throw new Error('ThaklisApiService not initialized. Call initForOrganization() first.');
    return thaklisClient.get(url, config);
  },

  post: (url, data, config = {}) => {
    if (!thaklisClient) throw new Error('ThaklisApiService not initialized. Call initForOrganization() first.');
    return thaklisClient.post(url, data, config);
  },

  put: (url, data, config = {}) => {
    if (!thaklisClient) throw new Error('ThaklisApiService not initialized. Call initForOrganization() first.');
    return thaklisClient.put(url, data, config);
  },

  delete: (url, config = {}) => {
    if (!thaklisClient) throw new Error('ThaklisApiService not initialized. Call initForOrganization() first.');
    return thaklisClient.delete(url, config);
  }
};

export default ThaklisApiService;
